/**
 * Призрак комнаты под курсором для инструмента «Комната»: где встанут стены и какой
 * получится комната.
 *
 * Полосы — тела стен, которые встанут, в их толщину; сторона, которую комната берёт у
 * существующей стены, полосы не имеет. Пунктир — чистый контур. Внутри — размеры
 * в чистоте по сторонам и площадь: ровно то, что потом покажет чертёж. Цвет — как у черновика
 * стены: перегородка синяя, несущая красная; комната задевает существующие стены — красный
 * пунктир, ставить нельзя. Мышь сквозь призрак проходит — клик ловит холст.
 */
import { BEARING_HATCH } from "@/shared/catalog";
import { formatArea, formatMillimeters } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";
import type { RoomGhost } from "../hooks/usePlaceRoom";
import type { RoomWallSegment } from "../roomLayout";

const PARTITION_COLOR = "#2f6feb";
const INVALID_COLOR = "#d64545";

interface Props {
	preview: RoomGhost;
	mmPerPx: number;
}

const pathOf = (points: { x: number; y: number }[]) =>
	`M${points.map((p) => `${p.x} ${p.y}`).join("L")}Z`;

export const RoomPreview = ({ preview, mmPerPx }: Props) => {
	const unit = useLengthUnit();
	const px = (n: number) => n * mmPerPx;
	const color = preview.blocked
		? INVALID_COLOR
		: preview.bearing
			? BEARING_HATCH.stroke
			: PARTITION_COLOR;
	/** Тело стены: прямоугольник вокруг отрезка оси в толщину стены */
	const body = ({ from, to, thickness }: RoomWallSegment) => {
		const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
		const nx = (-(to.y - from.y) / length) * (thickness / 2);
		const ny = ((to.x - from.x) / length) * (thickness / 2);
		return pathOf([
			{ x: from.x + nx, y: from.y + ny },
			{ x: to.x + nx, y: to.y + ny },
			{ x: to.x - nx, y: to.y - ny },
			{ x: from.x - nx, y: from.y - ny },
		]);
	};
	const [topLeft, topRight, , bottomLeft] = preview.inner;
	const cx = (topLeft.x + topRight.x) / 2;
	const cy = (topLeft.y + bottomLeft.y) / 2;
	const fontSize = px(12);
	// Узкая комната: длина у левой стены и площадь в центре наехали бы друг на друга —
	// площадь уходит ниже середины
	const narrow = preview.width / mmPerPx < 160;
	return (
		<g style={{ pointerEvents: "none" }}>
			{/* Тела стен, которые встанут */}
			<path
				d={preview.segments.map(body).join("")}
				fill={color}
				fillOpacity={0.22}
				stroke={color}
				strokeWidth={px(1.2)}
				strokeDasharray={preview.blocked ? `${px(6)} ${px(4)}` : undefined}
			/>
			{/* Чистый контур комнаты */}
			<path
				d={pathOf(preview.inner)}
				fill="none"
				stroke={color}
				strokeWidth={px(0.8)}
				strokeDasharray={`${px(4)} ${px(3)}`}
				opacity={0.7}
			/>
			<text
				x={cx}
				y={topLeft.y + px(16)}
				textAnchor="middle"
				fontSize={fontSize}
				fill={color}
				style={{ userSelect: "none" }}
			>
				{formatMillimeters(preview.width, unit)}
			</text>
			<text
				x={topLeft.x + px(8)}
				y={cy}
				fontSize={fontSize}
				fill={color}
				dominantBaseline="middle"
				style={{ userSelect: "none" }}
			>
				{formatMillimeters(preview.depth, unit)}
			</text>
			<text
				x={cx}
				y={narrow ? cy + px(22) : cy}
				textAnchor="middle"
				dominantBaseline="middle"
				fontSize={px(14)}
				fontWeight={600}
				fill={color}
				style={{ userSelect: "none" }}
			>
				{formatArea(preview.width * preview.depth)}
			</text>
		</g>
	);
};
